import { Icon } from '@chakra-ui/react';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { ListItem } from '@components/ui/atoms/ListItem';
import { Text } from '@components/ui/atoms/Text';
import { Tooltip } from '@components/ui/molecules/Tooltip';
import { useHover } from '@shared/hooks';
import { Link } from 'react-router';

import type { SidebarMenuItem } from './types';

interface Props {
  menuItem: SidebarMenuItem;
  isActive: boolean;
  isExpanded: boolean;
}

export const SingleItem: React.FC<Props> = ({ menuItem, isActive, isExpanded }) => {
  const { ref, isHovering } = useHover<HTMLDivElement>();
  const shouldHighlight = isActive || isHovering;
  const iconSize = '16px';

  const content = (
    <Flex
      justifyContent={isExpanded ? 'flex-start' : 'center'}
      alignItems="center"
      gap="8px"
      position="relative"
      px={isExpanded ? undefined : 0}
      _before={
        shouldHighlight
          ? {
              content: '""',
              position: 'absolute',
              left: isExpanded ? '-56px' : '-38px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '14px',
              backgroundColor: 'brand',
              borderRadius: '1px',
            }
          : {}
      }
    >
      {menuItem.icon && (
        <Icon
          display="flex"
          justifyContent="center"
          alignItems="center"
          css={{ width: iconSize, height: iconSize }}
          color={shouldHighlight ? 'brand' : 'body.dark'}
        >
          {menuItem.icon(shouldHighlight ? 'active' : 'inactive')}
        </Icon>
      )}
      {isExpanded && (
        <Text
          fontSize="12px"
          lineHeight="normal"
          color={shouldHighlight ? 'brand' : 'body.dark'}
          textStyle="bodyThree"
          fontWeight={shouldHighlight ? '700' : '500'}
        >
          {menuItem.label}
        </Text>
      )}
    </Flex>
  );

  const link = menuItem.url ? (
    <Link to={menuItem.url} replace={isActive} aria-label={!isExpanded ? menuItem.label : undefined}>
      {content}
    </Link>
  ) : (
    <Box
      as="button"
      w="full"
      bg="transparent"
      border="none"
      p="0"
      cursor="pointer"
      onClick={menuItem.onClick}
      aria-label={!isExpanded ? menuItem.label : undefined}
    >
      {content}
    </Box>
  );

  return (
    <Box ref={ref} position="relative">
      <ListItem _marker={{ color: shouldHighlight ? 'brand' : 'brand.light' }}>
        {isExpanded ? (
          link
        ) : (
          <Tooltip
            content={menuItem.label}
            positioning={{ placement: 'right' }}
            showArrow
          >
            <Box display="block" w="full">
              {link}
            </Box>
          </Tooltip>
        )}
      </ListItem>
    </Box>
  );
};

SingleItem.displayName = 'SingleItem';
